import { useMemo } from 'react';
import { NotificationType } from 'features/notifications/notifications.types';
import { ChildLocation } from 'features/locations/location.types';
import {
  Wildfire,
  WildfireCreateUpdateData,
} from 'features/wildfires/wildfire.types';
import { SubmittedWildfireEditData } from 'features/provisionalSubmissions/provisionalSubmissions.types';
import { ReportMedia } from 'features/reports/report.types';
import {
  reporterNotesChangesToFormValues,
  reporterNotesDefaultFormValues,
} from 'features/reporterNotes/reporterNotes.utils';
import { RelatedWildfire } from 'features/wildfires/wildfireGeoEventData.types';
import { GeoEventUrgencyLevel } from 'features/geoEvents/geoEvent.types';
import {
  getInitialEvacZonesVisibleState,
  parseFormLinks,
} from 'features/geoEvents/geoEvent.utils';
import { GeoEventFormStatus } from 'shared/types';
import { WildfireFormValues } from '../WildfireForm.types';

export type UseInitialValuesProps = {
  wildfire?: Wildfire;
  reviewSubmittedData?: WildfireCreateUpdateData | SubmittedWildfireEditData;
  reviewSubmissionMedia?: ReportMedia[];
  initialAddress?: string;
  initialStatus: GeoEventFormStatus;
  initialPrescribed: boolean;
  initialLatitude?: number;
  initialLongitude?: number;
  initialAlertCameraId?: string;
  initialExternalGeoEventId?: number;
};

export const convertProvisionalSubmissionDataToFormValues = (
  submittedData: WildfireCreateUpdateData | SubmittedWildfireEditData,
  submissionMedia?: ReportMedia[],
  wildfire?: Wildfire,
): WildfireFormValues => {
  const capturedMedia = submissionMedia?.[0];

  let mediaFields;
  if (capturedMedia) {
    mediaFields = {
      asset: 'image' as const,
      mediaUrl: capturedMedia.url,
      provisionalSubmissionMediaId: capturedMedia.id,
      mediaLat: capturedMedia.lat != null ? String(capturedMedia.lat) : '',
      mediaLng: capturedMedia.lng != null ? String(capturedMedia.lng) : '',
      mediaAz: capturedMedia.az ?? 0,
    };
  } else if (submittedData.initialReport?.embedUrl) {
    mediaFields = {
      asset: 'socialEmbed' as const,
      mediaLat: '',
      mediaLng: '',
      mediaAz: 0,
    };
  } else {
    mediaFields = { asset: null, mediaLat: '', mediaLng: '', mediaAz: 0 };
  }

  return {
    name: submittedData.name || '',
    // Omitted from an edit submission when unchanged.
    address: submittedData.address ?? wildfire?.address ?? '',
    regions: submittedData.regions ?? wildfire?.regions ?? [],
    status: submittedData.isActive ? 'active' : 'inactive',
    notificationType: submittedData.notificationType || NotificationType.Silent,
    sizeUnit: 'acres',
    acreage: submittedData.data?.acreage ?? null,
    containment: submittedData.data?.containment ?? null,
    isFps: Boolean(submittedData.data?.isFps),
    isPrescribed: Boolean(submittedData.data?.isPrescribed),
    reporterManaged: submittedData.reporterManaged ?? true,
    reportNotificationType: NotificationType.Normal,
    reportMessage: submittedData.initialReport?.message || '',
    lat: submittedData.lat ?? wildfire?.lat ?? 0,
    lng: submittedData.lng ?? wildfire?.lng ?? 0,
    prescribedDateStartLocal: submittedData.data?.prescribedDateStartLocal
      ? new Date(submittedData.data.prescribedDateStartLocal)
      : null,
    expandLinks: Boolean(submittedData.data?.links?.length),
    links: parseFormLinks(submittedData.data?.links),
    reporterOnlyNotes: submittedData.data?.reporterOnlyNotes || '',
    externalGeoEventId: submittedData.externalGeoEventId,
    expandInternalLinks: Boolean(submittedData.data?.internalLinks?.length),
    internalLinks: parseFormLinks(submittedData.data?.internalLinks ?? []),
    manualDeactivationStarted: Boolean(
      submittedData.data?.manualDeactivationStarted,
    ),
    urgency: submittedData.data?.urgency || GeoEventUrgencyLevel.Standard,
    ...reporterNotesChangesToFormValues(submittedData.reporterNotes),

    embedUrl: submittedData.initialReport?.embedUrl ?? '',
    media: null,
    ...mediaFields,

    // Evac zones and complexes are not editable by moderated reporters, but we need to render
    // them in the UI. So we pull them directly from the geo_event itself rather than
    // the submitted data
    activeEvacuations: getInitialEvacZonesVisibleState(wildfire),
    temporarilyDisplayEvacZones: Boolean(
      wildfire?.data.temporarilyDisplayEvacZones,
    ),
    evacuationOrders: wildfire?.data.evacuationOrders || '',
    evacuationWarnings: wildfire?.data.evacuationWarnings || '',
    evacuationAdvisories: wildfire?.data.evacuationAdvisories || '',
    evacuationShelterInPlace: wildfire?.data.evacuationShelterInPlace || '',
    evacuationNotes: wildfire?.data.evacuationNotes || '',
    evacZoneStatuses: wildfire?.evacZoneStatuses ?? [],
    unstructuredEvacuationRegionIds:
      wildfire?.data.unstructuredEvacuationRegionIds ?? [],
    customOrders: Boolean(wildfire?.data.hasCustomEvacuationOrders),
    customWarnings: Boolean(wildfire?.data.hasCustomEvacuationWarnings),
    customAdvisories: Boolean(wildfire?.data.hasCustomEvacuationAdvisories),
    customShelterInPlace: Boolean(
      wildfire?.data.hasCustomEvacuationShelterInPlace,
    ),
    isComplexParent: Boolean(wildfire?.data.isComplexParent),
    childLocations: (wildfire?.childGeoEvents?.filter(
      (ge) => ge.geoEventType === 'location',
    ) || []) as ChildLocation[],
    childWildfires: (wildfire?.childGeoEvents?.filter(
      (ge) => ge.geoEventType === 'wildfire',
    ) || []) as RelatedWildfire[],

    feetLength: null,
    feetWidth: null,
    fileType: '',
  };
};

export const convertWildfireValuesToFormValues = (
  props: UseInitialValuesProps,
): WildfireFormValues => {
  const {
    wildfire,
    reviewSubmittedData,
    reviewSubmissionMedia,
    initialAddress,
    initialStatus,
    initialPrescribed,
    initialLatitude,
    initialLongitude,
    initialAlertCameraId,
    initialExternalGeoEventId,
  } = props;

  if (reviewSubmittedData) {
    return convertProvisionalSubmissionDataToFormValues(
      reviewSubmittedData,
      reviewSubmissionMedia,
      wildfire,
    );
  }

  return {
    name: wildfire?.name || '',
    address: wildfire?.data?.isComplexParent
      ? ''
      : wildfire?.address || initialAddress || '',
    regions:
      wildfire?.data?.isComplexParent && wildfire?.childGeoEvents?.length
        ? []
        : wildfire?.regions || [],
    status: initialStatus,
    notificationType: wildfire?.notificationType || NotificationType.Silent,
    sizeUnit: 'acres',
    feetLength: null,
    feetWidth: null,
    acreage: wildfire?.data.acreage ?? null,
    containment: wildfire?.data.containment ?? null,
    isComplexParent: Boolean(wildfire?.data.isComplexParent),
    isFps: Boolean(wildfire?.data.isFps),
    isPrescribed: initialPrescribed,
    reporterManaged: wildfire?.reporterManaged ?? true,
    temporarilyDisplayEvacZones: Boolean(
      wildfire?.data.temporarilyDisplayEvacZones,
    ),
    evacuationOrders: wildfire?.data.evacuationOrders || '',
    evacuationWarnings: wildfire?.data.evacuationWarnings || '',
    evacuationAdvisories: wildfire?.data.evacuationAdvisories || '',
    evacuationShelterInPlace: wildfire?.data.evacuationShelterInPlace || '',
    evacuationNotes: wildfire?.data.evacuationNotes || '',
    reportNotificationType: NotificationType.Normal,
    reportMessage: '',
    lat: wildfire?.lat || initialLatitude || 0,
    lng: wildfire?.lng || initialLongitude || 0,
    prescribedDateStartLocal: wildfire?.data.prescribedDateStartLocal
      ? new Date(wildfire.data.prescribedDateStartLocal)
      : null,
    activeEvacuations: getInitialEvacZonesVisibleState(wildfire),
    evacZoneStatuses: wildfire?.evacZoneStatuses ?? [],
    unstructuredEvacuationRegionIds:
      wildfire?.data.unstructuredEvacuationRegionIds ?? [],
    customOrders: Boolean(wildfire?.data.hasCustomEvacuationOrders),
    customWarnings: Boolean(wildfire?.data.hasCustomEvacuationWarnings),
    customAdvisories: Boolean(wildfire?.data.hasCustomEvacuationAdvisories),
    customShelterInPlace: Boolean(
      wildfire?.data.hasCustomEvacuationShelterInPlace,
    ),
    asset: initialAlertCameraId ? 'alertCamera' : null,
    media: null,
    fileType: '',
    embedUrl: '',
    mediaLat: '',
    mediaLng: '',
    mediaAz: 0,
    expandLinks: Boolean(wildfire?.data.links?.length),
    links: parseFormLinks(wildfire?.data.links),
    reporterOnlyNotes: wildfire?.data.reporterOnlyNotes || '',
    childLocations: (wildfire?.childGeoEvents?.filter(
      (ge) => ge.geoEventType === 'location',
    ) || []) as ChildLocation[],
    childWildfires: (wildfire?.childGeoEvents?.filter(
      (ge) => ge.geoEventType === 'wildfire',
    ) || []) as RelatedWildfire[],
    manualDeactivationStarted:
      typeof wildfire?.data.manualDeactivationStarted === 'boolean'
        ? wildfire?.data.manualDeactivationStarted
        : null,
    externalGeoEventId: initialExternalGeoEventId,
    expandInternalLinks: Boolean(wildfire?.data.internalLinks?.length),
    internalLinks: parseFormLinks(wildfire?.data.internalLinks ?? []),
    urgency: wildfire?.data.urgency || GeoEventUrgencyLevel.Standard,
    ...reporterNotesDefaultFormValues,
  };
};

export const useInitialValues = (
  props: UseInitialValuesProps,
): WildfireFormValues => {
  const {
    wildfire,
    reviewSubmittedData,
    reviewSubmissionMedia,
    initialAddress,
    initialStatus,
    initialPrescribed,
    initialLatitude,
    initialLongitude,
    initialAlertCameraId,
    initialExternalGeoEventId,
  } = props;

  return useMemo(
    () =>
      convertWildfireValuesToFormValues({
        wildfire,
        reviewSubmittedData,
        reviewSubmissionMedia,
        initialAddress,
        initialStatus,
        initialPrescribed,
        initialLatitude,
        initialLongitude,
        initialAlertCameraId,
        initialExternalGeoEventId,
      }),
    [
      wildfire,
      reviewSubmittedData,
      reviewSubmissionMedia,
      initialAddress,
      initialStatus,
      initialPrescribed,
      initialLatitude,
      initialLongitude,
      initialAlertCameraId,
      initialExternalGeoEventId,
    ],
  );
};
